import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Pagination,
  Divider,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Map as MapIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import { CompanyMapService } from "../../services/CompanyMapService";

const NaverMapWithList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const panoramaRef = useRef(null);
  const panoramaInstance = useRef(null);

  const [allCompanies, setAllCompanies] = useState([]); // 전체 데이터
  const [visibleCompanies, setVisibleCompanies] = useState([]); // 현재 지도에 보이는 데이터
  const [visibleBranches, setVisibleBranches] = useState([]); // 현재 지도에 보이는 본부들
  const [displayMode, setDisplayMode] = useState("companies"); // 'companies' or 'branches'
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isUsingDummyData, setIsUsingDummyData] = useState(false);
  const [showPanorama, setShowPanorama] = useState(false);
  const [panoramaPosition, setPanoramaPosition] = useState(null);

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);

  // 지도 상태
  const [currentBounds, setCurrentBounds] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(7);

  // 네이버 지도 초기화
  const initializeMap = () => {
    if (!window.naver || !window.naver.maps) {
      console.error("네이버 지도 API가 로드되지 않았습니다.");
      return;
    }

    const defaultCenter = new window.naver.maps.LatLng(36.4800984, 127.289306);

    const mapOptions = {
      center: defaultCenter,
      zoom: 7,
      minZoom: 6,
      maxZoom: 18,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.naver.maps.MapTypeControlStyle.BUTTON,
        position: window.naver.maps.Position.TOP_RIGHT,
      },
      zoomControl: true,
      zoomControlOptions: {
        style: window.naver.maps.ZoomControlStyle.SMALL,
        position: window.naver.maps.Position.TOP_LEFT,
      },
      scaleControl: false,
      logoControl: false,
      mapDataControl: false,
    };

    mapInstance.current = new window.naver.maps.Map(mapRef.current, mapOptions);

    // 지도 이벤트 리스너 등록 (지도 생성 완료 후)
    setTimeout(() => {
      if (mapInstance.current) {
        window.naver.maps.Event.addListener(
          mapInstance.current,
          "bounds_changed",
          handleBoundsChanged
        );
        window.naver.maps.Event.addListener(
          mapInstance.current,
          "zoom_changed",
          handleZoomChanged
        );

        // 초기 bounds 설정
        const initialBounds = mapInstance.current.getBounds();
        setCurrentBounds(initialBounds);
      }
    }, 100);
  };

  // 지도 영역 변경 시 호출
  const handleBoundsChanged = useCallback(() => {
    if (!mapInstance.current) return;

    const bounds = mapInstance.current.getBounds();
    setCurrentBounds(bounds);

    // 디바운스를 위해 타이머 사용
    clearTimeout(window.boundsChangeTimer);
    window.boundsChangeTimer = setTimeout(() => {
      updateVisibleCompanies(bounds);
    }, 300);
  }, [allCompanies]);

  // 지도 영역 내 매장 필터링
  const updateVisibleCompanies = useCallback(
    (bounds) => {
      if (!bounds) {
        return;
      }

      if (!allCompanies.length) {
        return;
      }

      const filtered = allCompanies.filter((company) => {
        if (!company.latitude || !company.longitude) return false;

        const position = new window.naver.maps.LatLng(
          company.latitude,
          company.longitude
        );
        const isInBounds = bounds.hasLatLng(position);

        return isInBounds;
      });

      setVisibleCompanies(filtered);
      setTotalElements(filtered.length);
      setCurrentPage(1); // 영역 변경 시 첫 페이지로 리셋

      // 필터링된 매장들로 마커 업데이트
      createMarkers(filtered);
    },
    [allCompanies]
  );

  // 거리 기반 샘플링 (멀리 떨어진 매장들을 우선 선택)
  const sampleCompaniesByDistance = (companies, maxCount) => {
    if (companies.length <= maxCount) return companies;

    const sampled = [];
    const remaining = [...companies];

    // 첫 번째는 랜덤하게 선택
    if (remaining.length > 0) {
      const firstIndex = Math.floor(Math.random() * remaining.length);
      sampled.push(remaining.splice(firstIndex, 1)[0]);
    }

    // 나머지는 기존 선택된 점들과 가장 멀리 떨어진 점들을 선택
    while (sampled.length < maxCount && remaining.length > 0) {
      let maxDistance = 0;
      let bestIndex = 0;

      for (let i = 0; i < remaining.length; i++) {
        const minDistanceToSampled = Math.min(
          ...sampled.map((s) => getDistance(remaining[i], s))
        );

        if (minDistanceToSampled > maxDistance) {
          maxDistance = minDistanceToSampled;
          bestIndex = i;
        }
      }

      sampled.push(remaining.splice(bestIndex, 1)[0]);
    }

    return sampled;
  };

  // 두 점 사이의 거리 계산 (단순화된 유클리드 거리)
  const getDistance = (company1, company2) => {
    const dx = company1.latitude - company2.latitude;
    const dy = company1.longitude - company2.longitude;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // 본부별 그룹핑 함수
  const groupCompaniesByBranch = useCallback((companies) => {
    const groups = {};

    companies.forEach((company) => {
      const branchId = company.branchGroupId;
      if (!groups[branchId]) {
        groups[branchId] = {
          branchGroupId: branchId,
          branchGroupName: company.branchGroupName,
          companies: [],
          companyCount: 0,
          centerLatitude: 0,
          centerLongitude: 0,
          bounds: {
            minLat: Infinity,
            maxLat: -Infinity,
            minLng: Infinity,
            maxLng: -Infinity,
          },
        };
      }

      groups[branchId].companies.push(company);

      // 경계 업데이트
      if (company.latitude && company.longitude) {
        groups[branchId].bounds.minLat = Math.min(
          groups[branchId].bounds.minLat,
          company.latitude
        );
        groups[branchId].bounds.maxLat = Math.max(
          groups[branchId].bounds.maxLat,
          company.latitude
        );
        groups[branchId].bounds.minLng = Math.min(
          groups[branchId].bounds.minLng,
          company.longitude
        );
        groups[branchId].bounds.maxLng = Math.max(
          groups[branchId].bounds.maxLng,
          company.longitude
        );
      }
    });

    // 각 본부의 중심점과 개수 계산
    Object.values(groups).forEach((group) => {
      group.companyCount = group.companies.length;

      if (group.companies.length > 0) {
        const validCompanies = group.companies.filter(
          (c) => c.latitude && c.longitude
        );
        if (validCompanies.length > 0) {
          group.centerLatitude =
            validCompanies.reduce((sum, c) => sum + c.latitude, 0) /
            validCompanies.length;
          group.centerLongitude =
            validCompanies.reduce((sum, c) => sum + c.longitude, 0) /
            validCompanies.length;
        }
      }
    });

    const result = Object.values(groups);

    return result;
  }, []);

  // 마커 생성 및 표시
  const createMarkers = (companiesData) => {
    if (!mapInstance.current) {
      return;
    }

    // 기존 마커들 제거
    clearMarkers();

    const markers = [];

    companiesData.forEach((company) => {
      if (company.latitude && company.longitude) {
        const position = new window.naver.maps.LatLng(
          company.latitude,
          company.longitude
        );

        // 줌 레벨에 따른 마커 스타일 조정
        const markerSize = currentZoom < 8 ? 20 : 24;
        const innerSize = currentZoom < 8 ? 6 : 8;

        const marker = new window.naver.maps.Marker({
          position: position,
          map: mapInstance.current,
          title: company.storeName,
          icon: {
            content: `
              <div style="
                background: #3182F6;
                border: 3px solid white;
                border-radius: 50%;
                width: ${markerSize}px;
                height: ${markerSize}px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                cursor: pointer;
                transition: all 0.2s ease;
              " onmouseover="this.style.transform='scale(1.2)'" onmouseout="this.style.transform='scale(1)'">
                <div style="
                  width: ${innerSize}px;
                  height: ${innerSize}px;
                  background: white;
                  border-radius: 50%;
                "></div>
              </div>
            `,
            size: new window.naver.maps.Size(markerSize + 6, markerSize + 6),
            anchor: new window.naver.maps.Point(
              (markerSize + 6) / 2,
              (markerSize + 6) / 2
            ),
          },
        });

        // 마커 클릭 이벤트
        window.naver.maps.Event.addListener(marker, "click", () => {
          setSelectedCompany(company);

          // 선택된 매장만 목록에 표시 (집중 모드)
          setVisibleCompanies([company]);
          setTotalElements(1);
          setCurrentPage(1);

          // 해당 매장만 마커로 표시
          clearMarkers();
          createMarkers([company]);

          // 새로 생성된 마커에 정보창 표시 (비동기 처리)
          setTimeout(() => {
            const newMarker = markersRef.current.find((m) => {
              const markerPos = m.getPosition();
              return (
                Math.abs(markerPos.lat() - company.latitude) < 0.0001 &&
                Math.abs(markerPos.lng() - company.longitude) < 0.0001
              );
            });
            if (newMarker) {
              showInfoWindow(newMarker, company);
            }
          }, 50);
        });

        markers.push(marker);
      }
    });

    markersRef.current = markers;
  };

  // 본부별 클러스터 마커 생성
  const createBranchClusterMarkers = (branchGroups) => {
    if (!mapInstance.current) {
      return;
    }

    // 기존 마커들 제거
    clearMarkers();

    const markers = [];

    branchGroups.forEach((branch) => {
      if (branch.centerLatitude && branch.centerLongitude) {
        const position = new window.naver.maps.LatLng(
          branch.centerLatitude,
          branch.centerLongitude
        );

        // 클러스터 마커 스타일 (크기는 매장 수에 비례)
        const clusterSize = Math.min(
          Math.max(30 + branch.companyCount * 0.5, 40),
          80
        );
        const fontSize = Math.min(Math.max(10, branch.companyCount * 0.2), 16);

        const marker = new window.naver.maps.Marker({
          position: position,
          map: mapInstance.current,
          title: `${branch.branchGroupName} (${branch.companyCount}개)`,
          icon: {
            content: `
              <div style="
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border: 3px solid white;
                border-radius: 50%;
                width: ${clusterSize}px;
                height: ${clusterSize}px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                cursor: pointer;
                transition: all 0.3s ease;
                color: white;
                font-weight: bold;
                text-align: center;
                font-family: 'Pretendard', sans-serif;
              " onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
                <div style="font-size: ${fontSize}px; line-height: 1; margin-bottom: 2px;">
                  ${branch.companyCount}
                </div>
                <div style="font-size: ${Math.max(
                  fontSize - 4,
                  8
                )}px; line-height: 1; opacity: 0.9;">
                  개 매장
                </div>
              </div>
            `,
            size: new window.naver.maps.Size(clusterSize + 6, clusterSize + 6),
            anchor: new window.naver.maps.Point(
              (clusterSize + 6) / 2,
              (clusterSize + 6) / 2
            ),
          },
        });

        // 클러스터 마커 클릭 이벤트
        window.naver.maps.Event.addListener(marker, "click", () => {
          // 기존 정보창 닫기
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            infoWindowRef.current = null;
          }

          setSelectedBranch(branch);
          setSelectedCompany(null);

          // 해당 본부 영역으로 줌 인
          zoomToBranchArea(branch);
        });

        markers.push(marker);
      }
    });

    markersRef.current = markers;
  };

  // 파노라마 열기
  const openPanorama = (company) => {
    if (!window.naver || !window.naver.maps) {
      console.error("네이버 지도 API가 로드되지 않았습니다.");
      alert(
        "네이버 지도 API가 로드되지 않았습니다. 페이지를 새로고침해주세요."
      );
      return;
    }

    if (!window.naver.maps.Panorama) {
      console.error("네이버 지도 Panorama 모듈이 로드되지 않았습니다.");
      alert("파노라마 모듈이 로드되지 않았습니다. 페이지를 새로고침해주세요.");
      return;
    }

    const position = new window.naver.maps.LatLng(
      company.latitude,
      company.longitude
    );

    setPanoramaPosition(position);
    setShowPanorama(true);

    // 파노라마 인스턴스 생성 (약간의 지연을 두어 DOM이 준비된 후 생성)
    setTimeout(() => {
      if (panoramaRef.current) {
        try {
          // 기존 인스턴스 제거
          if (panoramaInstance.current) {
            panoramaInstance.current = null;
          }

          panoramaInstance.current = new window.naver.maps.Panorama(
            panoramaRef.current,
            {
              position: position,
              pov: {
                pan: 0,
                tilt: 0,
                fov: 100,
              },
              flightSpot: false,
              logoControl: false,
              logoControlOptions: {
                position: window.naver.maps.Position.BOTTOM_RIGHT,
              },
              zoomControl: true,
              zoomControlOptions: {
                position: window.naver.maps.Position.TOP_LEFT,
              },
            }
          );
        } catch (error) {
          console.error("파노라마 생성 실패:", error);
          alert(`파노라마 생성 실패: ${error.message}`);
        }
      } else {
        console.error("파노라마 DOM 컨테이너를 찾을 수 없습니다.");
      }
    }, 500); // 지연 시간을 늘림
  };

  // 파노라마 닫기
  const closePanorama = () => {
    setShowPanorama(false);
    setPanoramaPosition(null);
    if (panoramaInstance.current) {
      panoramaInstance.current = null;
    }
  };

  // 정보창 표시
  const showInfoWindow = (marker, company) => {
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    const infoWindowContent = `
      <div style="position: relative; font-family: 'Pretendard', sans-serif; background: white; border-radius: 8px; overflow: hidden;">
        <div style="padding: 15px; min-width: 250px; padding-right: 40px;">
          <button onclick="closeInfoWindow()" style="
            position: absolute;
            top: 10px;
            right: 10px;
            background: rgba(0,0,0,0.1);
            border: none;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            color: #666;
            padding: 0;
            line-height: 1;
            z-index: 1000;
            transition: all 0.2s ease;
          " onmouseover="this.style.backgroundColor='rgba(0,0,0,0.2)'; this.style.color='#333';" onmouseout="this.style.backgroundColor='rgba(0,0,0,0.1)'; this.style.color='#666';">×</button>
          <div style="font-size: 16px; font-weight: 600; color: #1976d2; margin-bottom: 8px;">
            ${company.storeName}
          </div>
          <div style="font-size: 14px; color: #333; margin-bottom: 6px;">
            <strong>수탁자:</strong> ${company.representativeName}
          </div>
          <div style="font-size: 14px; color: #333; margin-bottom: 6px;">
            <strong>연락처:</strong> ${company.storeTelNumber || "-"}
          </div>
          <div style="font-size: 14px; color: #333; margin-bottom: 8px;">
            <strong>주소:</strong> ${company.companyAddress}
          </div>
          <div style="font-size: 12px; color: #666; margin-bottom: 10px;">
            본부: ${company.branchGroupName || "미분류"}
          </div>
          <div style="display: flex; gap: 8px;">
            <button onclick="openPanoramaFromInfo('${company.id}')" style="
              background: transparent;
              color: #4CAF50;
              border: 1px solid #4CAF50;
              border-radius: 4px;
              padding: 6px 12px;
              font-size: 12px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              font-family: 'Pretendard', sans-serif;
              display: flex;
              align-items: center;
              gap: 4px;
            " onmouseover="this.style.backgroundColor='rgba(76, 175, 80, 0.1)'; this.style.color='#45a049';" onmouseout="this.style.backgroundColor='transparent'; this.style.color='#4CAF50';">
              거리뷰 보기
            </button>
          </div>
        </div>
      </div>
    `;

    infoWindowRef.current = new window.naver.maps.InfoWindow({
      content: infoWindowContent,
      backgroundColor: "white",
      borderColor: "#ddd",
      borderWidth: 1,
      anchorSize: new window.naver.maps.Size(15, 15),
      anchorSkew: true,
      anchorColor: "white",
      pixelOffset: new window.naver.maps.Point(0, -10),
    });

    infoWindowRef.current.open(mapInstance.current, marker);
  };

  // 본부 영역으로 줌 인
  const zoomToBranchArea = (branch) => {
    if (!mapInstance.current || !branch.bounds) return;

    const bounds = new window.naver.maps.LatLngBounds(
      new window.naver.maps.LatLng(branch.bounds.minLat, branch.bounds.minLng),
      new window.naver.maps.LatLng(branch.bounds.maxLat, branch.bounds.maxLng)
    );

    mapInstance.current.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 50,
      left: 50,
    });

    // 줌 인 후 개별 매장 표시 모드로 전환
    setTimeout(() => {
      setDisplayMode("companies");
      setVisibleCompanies(branch.companies);
      setTotalElements(branch.companies.length);
      setCurrentPage(1);
      createMarkers(branch.companies);
    }, 500);
  };

  // 마커 제거
  const clearMarkers = () => {
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current = [];

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  };

  // 전체 매장 데이터 로드
  const loadAllCompanies = async () => {
    try {
      setLoading(true);
      let data;

      try {
        // 실제 API 호출 시도 (페이징 없이 전체 데이터)
        data = await CompanyMapService.getAllCompaniesForMap(true);
        setIsUsingDummyData(false);
      } catch (apiError) {
        console.warn(
          "실제 API 연결 실패, 테스트 데이터 사용:",
          apiError.message
        );
        // API 연결 실패 시 더미 데이터 사용
        data = CompanyMapService.getDummyCompaniesForTesting();
        setIsUsingDummyData(true);
      }

      setAllCompanies(data);

      // 초기 지도 영역 설정을 위해 모든 매장이 보이도록 bounds 계산
      if (data.length > 0 && mapInstance.current) {
        const bounds = new window.naver.maps.LatLngBounds();
        data.forEach((company) => {
          if (company.latitude && company.longitude) {
            bounds.extend(
              new window.naver.maps.LatLng(company.latitude, company.longitude)
            );
          }
        });

        mapInstance.current.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50,
        });

        // bounds 설정 후 현재 줌 레벨에 따른 표시
        setTimeout(() => {
          const currentZoom = mapInstance.current.getZoom();
          setCurrentZoom(currentZoom);

          // 데이터를 직접 전달하여 표시 모드 설정
          if (currentZoom < 8) {
            const branchGroups = groupCompaniesByBranch(data);
            setDisplayMode("branches");
            setVisibleBranches(branchGroups);
            setTotalElements(branchGroups.length);
            setCurrentPage(1);
            createBranchClusterMarkers(branchGroups);
          } else {
            setDisplayMode("companies");
            setVisibleCompanies(data);
            setTotalElements(data.length);
            setCurrentPage(1);
            createMarkers(data);
          }
        }, 500);
      }
    } catch (error) {
      console.error("매장 데이터 로드 실패:", error);
      const dummyData = CompanyMapService.getDummyCompaniesForTesting();
      setAllCompanies(dummyData);
      setIsUsingDummyData(true);

      // 더미 데이터도 줌 레벨에 따른 표시
      if (mapInstance.current) {
        setTimeout(() => {
          const currentZoom = mapInstance.current.getZoom();
          setCurrentZoom(currentZoom);
          // 더미 데이터를 직접 전달하여 표시 모드 설정
          if (currentZoom < 8) {
            const branchGroups = groupCompaniesByBranch(dummyData);
            setDisplayMode("branches");
            setVisibleBranches(branchGroups);
            setTotalElements(branchGroups.length);
            setCurrentPage(1);
            createBranchClusterMarkers(branchGroups);
          } else {
            setDisplayMode("companies");
            setVisibleCompanies(dummyData);
            setTotalElements(dummyData.length);
            setCurrentPage(1);
            createMarkers(dummyData);
          }
        }, 500);
      }
    } finally {
      setLoading(false);
    }
  };

  // 검색 처리
  const handleSearch = (event) => {
    if (event.key === "Enter" || event.type === "click") {
      filterCompaniesBySearch(searchKeyword);
    }
  };

  // 검색 필터링
  const filterCompaniesBySearch = (searchTerm) => {
    if (!searchTerm) {
      // 검색어가 없으면 현재 지도 영역의 모든 매장 표시
      if (currentBounds) {
        updateVisibleCompanies(currentBounds);
      }
      return;
    }

    const filtered = allCompanies.filter(
      (company) =>
        company.storeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.companyAddress
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        company.representativeName
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );

    setVisibleCompanies(filtered);
    setTotalElements(filtered.length);
    setCurrentPage(1);
    createMarkers(filtered);
  };

  // 리스트에서 매장 클릭
  const handleCompanyClick = (company) => {
    setSelectedCompany(company);

    // 선택된 매장만 목록에 표시 (집중 모드)
    setVisibleCompanies([company]);
    setTotalElements(1);
    setCurrentPage(1);

    // 지도 중심을 해당 매장으로 이동
    if (mapInstance.current && company.latitude && company.longitude) {
      const position = new window.naver.maps.LatLng(
        company.latitude,
        company.longitude
      );
      mapInstance.current.setCenter(position);
      mapInstance.current.setZoom(15); // 상세 보기를 위해 줌 인

      // 해당 매장만 마커로 표시
      clearMarkers();
      createMarkers([company]);

      // 새로 생성된 마커에 정보창 표시 (비동기 처리)
      setTimeout(() => {
        const marker = markersRef.current.find((m) => {
          const markerPos = m.getPosition();
          return (
            Math.abs(markerPos.lat() - company.latitude) < 0.0001 &&
            Math.abs(markerPos.lng() - company.longitude) < 0.0001
          );
        });

        if (marker) {
          showInfoWindow(marker, company);
        }
      }, 50);
    }
  };

  // 페이지네이션 - 현재 표시 모드에 따라 다른 데이터 반환
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    if (displayMode === "branches") {
      return visibleBranches.slice(startIndex, endIndex);
    } else {
      return visibleCompanies.slice(startIndex, endIndex);
    }
  };

  // 본부 클릭 핸들러
  const handleBranchClick = (branch) => {
    setSelectedBranch(branch);
    setSelectedCompany(null);
    zoomToBranchArea(branch);
  };

  // 전체 본부 보기로 돌아가기
  const handleBackToBranches = () => {
    setSelectedBranch(null);
    setSelectedCompany(null);

    // 전체 지도 영역으로 돌아가기
    if (mapInstance.current && allCompanies.length > 0) {
      const bounds = new window.naver.maps.LatLngBounds();
      allCompanies.forEach((company) => {
        if (company.latitude && company.longitude) {
          bounds.extend(
            new window.naver.maps.LatLng(company.latitude, company.longitude)
          );
        }
      });

      mapInstance.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      });

      // 줌 아웃 후 본부 클러스터 표시
      setTimeout(() => {
        const branchGroups = groupCompaniesByBranch(allCompanies);
        setDisplayMode("branches");
        setVisibleBranches(branchGroups);
        setTotalElements(branchGroups.length);
        setCurrentPage(1);
        createBranchClusterMarkers(branchGroups);
      }, 500);
    }
  };

  // 매장 목록으로 돌아가기 (매장 선택 해제)
  const handleBackToCompanyList = () => {
    setSelectedCompany(null);

    // 선택된 본부의 전체 매장 목록으로 복원
    if (selectedBranch) {
      const branchCompanies = allCompanies.filter(
        (company) => company.branchGroupId === selectedBranch.branchGroupId
      );
      setVisibleCompanies(branchCompanies);
      setTotalElements(branchCompanies.length);
      setCurrentPage(1);

      // 모든 매장 마커 다시 표시
      clearMarkers();
      createMarkers(branchCompanies);

      // 선택된 본부의 매장들이 보이도록 지도 줌 조정
      if (mapInstance.current) {
        zoomToBranchArea(selectedBranch);
      }
    }
  };

  // 줌 레벨에 따른 표시 모드 결정
  const updateDisplayForZoomLevel = useCallback(
    (zoom) => {
      if (!allCompanies.length) {
        return;
      }

      if (zoom < 8) {
        // 줌 아웃: 본부별 클러스터 표시
        const branchGroups = groupCompaniesByBranch(allCompanies);
        setDisplayMode("branches");
        setVisibleBranches(branchGroups);
        setTotalElements(branchGroups.length);
        setCurrentPage(1);
        createBranchClusterMarkers(branchGroups);
      } else {
        // 줌 인: 개별 매장 표시
        if (allCompanies.length > 0) {
          const currentBounds = mapInstance.current
            ? mapInstance.current.getBounds()
            : null;
          if (currentBounds) {
            const filtered = allCompanies.filter((company) => {
              if (!company.latitude || !company.longitude) return false;
              const position = new window.naver.maps.LatLng(
                company.latitude,
                company.longitude
              );
              return currentBounds.hasLatLng(position);
            });

            setDisplayMode("companies");
            setVisibleCompanies(filtered);
            setTotalElements(filtered.length);
            setCurrentPage(1);
            createMarkers(filtered);
          }
        }
      }
    },
    [
      allCompanies,
      groupCompaniesByBranch,
      createBranchClusterMarkers,
      createMarkers,
    ]
  );

  // 줌 레벨 변경 시 호출
  const handleZoomChanged = useCallback(() => {
    if (!mapInstance.current) return;

    const zoom = mapInstance.current.getZoom();
    setCurrentZoom(zoom);

    // 데이터가 있을 때만 표시 모드 업데이트
    if (allCompanies.length > 0) {
      updateDisplayForZoomLevel(zoom);
    }
  }, [allCompanies, updateDisplayForZoomLevel]);

  // 전역 함수 등록을 위한 별도 useEffect
  useEffect(() => {
    // 파노라마 열기 함수를 전역으로 등록 (allCompanies가 변경될 때마다 업데이트)
    window.openPanoramaFromInfo = (companyId) => {
      const company = allCompanies.find((c) => c.id === parseInt(companyId));

      if (company) {
        openPanorama(company);
        // 정보창 닫기
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
      } else {
        console.error("해당 ID의 회사를 찾을 수 없습니다:", companyId);
        alert("해당 매장 정보를 찾을 수 없습니다.");
      }
    };
  }, [allCompanies, openPanorama]);

  // 컴포넌트 마운트 시 지도 초기화 및 데이터 로드 (한 번만 실행)
  useEffect(() => {
    // InfoWindow 닫기 함수를 전역으로 등록
    window.closeInfoWindow = () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
    };

    const timer = setTimeout(() => {
      if (window.naver && window.naver.maps) {
        initializeMap();
        loadAllCompanies();
      } else {
        console.error("네이버 지도 API가 아직 로드되지 않았습니다.");
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (window.boundsChangeTimer) {
        clearTimeout(window.boundsChangeTimer);
      }
      // 컴포넌트 언마운트 시 전역 함수 제거
      if (window.closeInfoWindow) {
        delete window.closeInfoWindow;
      }
      if (window.openPanoramaFromInfo) {
        delete window.openPanoramaFromInfo;
      }
    };
  }, []); // 빈 의존성 배열로 한 번만 실행

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* 검색 바 */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="매장명, 회사주소, 수탁자명으로 검색..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyPress={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} size="small">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              height: "36px", // 검색창 높이 줄임
            },
            "& .MuiOutlinedInput-input": {
              padding: "8px 14px", // 내부 패딩 조정
            },
          }}
        />

        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon sx={{ fontSize: 18, color: "#666" }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", fontSize: "16px" }}
          >
            전체:{" "}
            <span style={{ color: "#ED1C24" }}>{allCompanies.length}</span>개
            매장 {" | "}
            {displayMode === "branches" ? (
              <>
                본부:{" "}
                <span style={{ color: "#ED1C24" }}>
                  {visibleBranches.length}
                </span>
                개
              </>
            ) : (
              <>
                표시:{" "}
                <span style={{ color: "#ED1C24" }}>
                  {visibleCompanies.length}
                </span>
                개 매장
              </>
            )}
          </Typography>
          {isUsingDummyData && (
            <Typography
              variant="body2"
              sx={{ color: "#ff9800", fontSize: "12px", fontWeight: 500 }}
            >
              (테스트 데이터)
            </Typography>
          )}
          {loading && <CircularProgress size={18} />}
        </Box>
      </Paper>

      {/* 메인 컨텐츠 영역 */}
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: "hidden" }}>
        {/* 지도 영역 */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              height: "100%",
              maxHeight: "calc(100vh - 280px)", // 리스트와 동일한 높이 제한
              minHeight: "450px", // 리스트와 동일한 최소 높이
            }}
          >
            <div
              ref={mapRef}
              style={{
                width: "100%",
                height: "100%",
                minHeight: "450px", // Paper와 동일한 최소 높이
              }}
            />

            {/* 파노라마 패널 */}
            {showPanorama && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 20,
                  right: 20,
                  width: { xs: 320, sm: 480, md: 520 }, // 반응형 크기
                  height: { xs: 240, sm: 360, md: 390 }, // 반응형 크기
                  maxWidth: "calc(100% - 40px)", // 컨테이너 넘지 않게
                  maxHeight: "calc(100% - 40px)", // 컨테이너 넘지 않게
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  overflow: "hidden",
                  zIndex: 1001,
                  border: "2px solid #1976d2",
                }}
              >
                {/* 파노라마 헤더 */}
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "#1976d2",
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    🌐
                  </Typography>
                  <IconButton
                    onClick={closePanorama}
                    size="small"
                    sx={{
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    ×
                  </IconButton>
                </Box>

                {/* 파노라마 컨테이너 */}
                <div
                  ref={panoramaRef}
                  style={{
                    width: "100%",
                    height: "calc(100% - 50px)",
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </Box>
            )}

            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1000,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* 매장 리스트 영역 */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 280px)", // 페이징 버튼 공간 확보를 위해 높이 조정
              minHeight: "450px", // 최소 높이 늘림 (페이징 버튼 공간 확보)
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #eee", flexShrink: 0 }}>
              {/* 헤더 영역 - 뒤로가기 버튼과 제목 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {displayMode === "branches"
                    ? `본부 목록 (${visibleBranches.length})`
                    : selectedCompany
                    ? "선택 매장"
                    : `매장 목록 (${visibleCompanies.length})`}
                </Typography>

                {/* 뒤로가기 버튼 */}
                {displayMode === "companies" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* 매장이 선택된 경우: 매장 목록으로 돌아가기 버튼만 표시 */}
                    {selectedCompany ? (
                      <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToCompanyList}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "12px",
                          borderColor: "#e3f2fd",
                          color: "#1976d2",
                          backgroundColor: "#f3f8ff",
                          "&:hover": {
                            borderColor: "#bbdefb",
                            backgroundColor: "#e3f2fd",
                          },
                        }}
                      >
                        매장목록
                      </Button>
                    ) : (
                      /* 매장이 선택되지 않은 경우: 본부 보기로 돌아가기 버튼만 표시 */
                      <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToBranches}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "12px",
                          borderColor: "#e0e0e0",
                          color: "#666",
                          "&:hover": {
                            borderColor: "#bdbdbd",
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        본부목록
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* 설명 텍스트 */}
              <Typography variant="caption" color="text.secondary">
                {displayMode === "branches"
                  ? "본부를 클릭하면 해당 지역으로 이동합니다"
                  : selectedCompany
                  ? '"매장목록" 버튼을 클릭하면 매장 목록으로 돌아갑니다'
                  : "매장을 클릭하면 상세 정보를 확인할 수 있습니다"}
              </Typography>
            </Box>

            <List
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: 0,
                maxHeight: "calc(100% - 140px)", // 페이징 버튼을 위한 공간 더 확보
                minHeight: "250px", // 리스트 최소 높이 보장
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#a8a8a8",
                },
              }}
            >
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((item, index) => (
                  <React.Fragment
                    key={
                      displayMode === "branches" ? item.branchGroupId : item.id
                    }
                  >
                    <ListItemButton
                      onClick={() =>
                        displayMode === "branches"
                          ? handleBranchClick(item)
                          : handleCompanyClick(item)
                      }
                      selected={
                        displayMode === "branches"
                          ? selectedBranch?.branchGroupId === item.branchGroupId
                          : selectedCompany?.id === item.id
                      }
                      sx={{
                        py: 1.5,
                        pr: displayMode === "companies" ? 0.5 : 2, // 매장 모드일 때 우측 패딩 줄임
                        "&.Mui-selected": {
                          backgroundColor:
                            displayMode === "branches" ? "#f3e5f5" : "#e3f2fd",
                        },
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {displayMode === "branches"
                              ? item.branchGroupName
                              : item.storeName}
                          </Typography>
                        }
                        secondary={
                          displayMode === "branches" ? (
                            <Box component="div">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                총 {item.companyCount}개 매장
                              </Typography>
                            </Box>
                          ) : (
                            <Box component="div">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                수탁자: {item.representativeName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                📍 {item.companyAddress}
                              </Typography>
                            </Box>
                          )
                        }
                        secondaryTypographyProps={{ component: "div" }}
                      />

                      {/* 매장 모드일 때만 거리뷰 버튼 표시 */}
                      {displayMode === "companies" && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation(); // 리스트 아이템 클릭 이벤트 중단
                            openPanorama(item);
                          }}
                          sx={{
                            color: "#4CAF50",
                            width: 32,
                            height: 32,
                            ml: 1,
                            "&:hover": {
                              backgroundColor: "rgba(76, 175, 80, 0.1)",
                              color: "#45a049",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                          title="거리뷰 보기"
                        >
                          <PlaceIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemButton>
                    {index < getCurrentPageItems().length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {displayMode === "branches"
                      ? "표시할 본부가 없습니다."
                      : "현재 지도 영역에 매장이 없습니다."}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {displayMode === "branches"
                      ? "지도를 확인해보세요."
                      : "지도를 이동하거나 줌 아웃해보세요."}
                  </Typography>
                </Box>
              )}
            </List>

            {/* 페이지네이션 */}
            {totalElements > pageSize && (
              <Box
                sx={{
                  p: 1.5, // 패딩 조금 줄임
                  borderTop: "1px solid #eee",
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0, // 페이징 영역은 절대 줄어들지 않도록
                  backgroundColor: "#fafafa",
                  minHeight: "60px", // 페이징 영역 최소 높이 보장
                  alignItems: "center", // 수직 중앙 정렬
                }}
              >
                <Pagination
                  count={Math.ceil(totalElements / pageSize)}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPagination-ul": {
                      flexWrap: "wrap", // 페이지 버튼이 많을 때 줄바꿈 허용
                      justifyContent: "center",
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 선택된 매장 정보 (하단) */}
      {selectedCompany && (
        <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            선택된 매장 정보
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <Chip
                icon={<BusinessIcon />}
                label={`${selectedCompany.storeName}`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Chip
                label={`수탁자: ${selectedCompany.representativeName}`}
                variant="outlined"
                size="small"
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Chip
                icon={<PhoneIcon />}
                label={selectedCompany.phoneNumber}
                variant="outlined"
                size="small"
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Chip
                label={selectedCompany.branchGroupName || "미분류"}
                variant="outlined"
                size="small"
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666", flex: 1, mr: 1 }}>
              📍 {selectedCompany.companyAddress}
            </Typography>
            <IconButton
              onClick={() => openPanorama(selectedCompany)}
              sx={{
                color: "#4CAF50",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  color: "#45a049",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
              title="거리뷰 보기"
            >
              <PlaceIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default NaverMapWithList;
